import React, { FC, FormEvent, useEffect, useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { CrmSalesStatus } from 'src/redux/slices/crm-sales/types';
import { crmSalesStatuses } from 'src/redux/slices/crm-sales';
import { CrmTicket } from './CrmTicketCard';

export type CrmFormValues = {
  customer_name: string;
  contact_no: string;
  address: string;
  service_type: string;
  status: CrmSalesStatus;
  deal_value: string;
  remarks: string;
  social_media_link: string;
};

interface Props {
  open: boolean;
  ticket?: CrmTicket | null;
  loading?: boolean;
  isReadOnly?: boolean;
  onClose: () => void;
  onSubmit: (values: CrmFormValues) => void | Promise<void>;
}

const DEFAULT_VALUES: CrmFormValues = {
  customer_name: '',
  contact_no: '',
  address: '',
  service_type: '',
  status: 'Lead',
  deal_value: '',
  remarks: '',
  social_media_link: ''
};

const SERVICE_TYPES = ['Build', 'Parts-out'];

const getStatusValue = (status?: string): CrmSalesStatus => {
  const matchedStatus = crmSalesStatuses.find((item) => item === status);
  return matchedStatus || 'Lead';
};

const getInitialValues = (ticket?: CrmTicket | null): CrmFormValues => ({
  customer_name: ticket?.customer_name || '',
  contact_no: ticket?.contact_no || '',
  address: ticket?.address || '',
  service_type: ticket?.service_type || '',
  status: getStatusValue(ticket?.status),
  deal_value:
    ticket?.deal_value !== undefined && ticket?.deal_value !== null
      ? String(ticket.deal_value)
      : '',
  remarks: ticket?.remarks || '',
  social_media_link: ticket?.social_media_link || ''
});

const getNextStatus = (status: CrmSalesStatus): CrmSalesStatus | undefined => {
  if (status === 'Close Deal') return;

  const statusIndex = crmSalesStatuses.indexOf(status);
  return crmSalesStatuses[statusIndex + 1];
};

export const CrmFormDialog: FC<Props> = ({
  open,
  ticket,
  loading = false,
  isReadOnly = false,
  onClose,
  onSubmit
}) => {
  const [values, setValues] = useState<CrmFormValues>(DEFAULT_VALUES);
  const nextStatus = getNextStatus(values.status);

  useEffect(() => {
    if (open) {
      setValues(getInitialValues(ticket));
    } else {
      setValues(DEFAULT_VALUES);
    }
  }, [open, ticket]);

  const handleChange = useCallback(
    (field: string) => (event: any) => {
      const rawValue = event.target.value;
      const value =
        field === 'deal_value'
          ? rawValue === '' || /^\d*$/.test(rawValue)
            ? rawValue
            : values.deal_value
          : rawValue;
      setValues((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    [values.deal_value]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      await onSubmit(values);
    },
    [onSubmit, values]
  );

  const handleProceedToNextStatus = useCallback(async () => {
    if (!nextStatus) return;

    await onSubmit({
      ...values,
      status: nextStatus
    });
  }, [nextStatus, onSubmit, values]);

  const handleProceedToLost = useCallback(async () => {
    await onSubmit({
      ...values,
      status: 'Lost'
    });
  }, [onSubmit, values]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle
          disableTypography
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">
            {ticket?.id ? 'CRM Ticket Details' : 'Add CRM Ticket'}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {ticket?.id && nextStatus && (
              <Button
                type="button"
                color="primary"
                variant="contained"
                disabled={loading}
                onClick={handleProceedToNextStatus}
              >
                Proceed to {nextStatus}
              </Button>
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Customer Name"
                name="customer_name"
                variant="outlined"
                value={values.customer_name}
                onChange={handleChange('customer_name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact No."
                name="contact_no"
                variant="outlined"
                value={values.contact_no}
                onChange={handleChange('contact_no')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                required
                fullWidth
                label="Service Type"
                name="service_type"
                variant="outlined"
                value={values.service_type}
                onChange={handleChange('service_type')}
              >
                {SERVICE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                variant="outlined"
                value={values.address}
                onChange={handleChange('address')}
              />
            </Grid>
            {isReadOnly ? null : (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  required
                  fullWidth
                  label="Status"
                  name="status"
                  variant="outlined"
                  value={values.status}
                  onChange={handleChange('status')}
                  InputProps={{ readOnly: isReadOnly }}
                >
                  {crmSalesStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deal Value"
                name="deal_value"
                type="number"
                inputProps={{
                  min: 0,
                  step: 1,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                variant="outlined"
                value={values.deal_value}
                onChange={handleChange('deal_value')}
              />
            </Grid>

            <Grid item xs={12} sm={isReadOnly ? 6 : 12}>
              <TextField
                fullWidth
                label="Social Media Link"
                name="social_media_link"
                variant="outlined"
                value={values.social_media_link}
                onChange={handleChange('social_media_link')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Remarks"
                name="remarks"
                variant="outlined"
                value={values.remarks}
                onChange={handleChange('remarks')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingLeft: 25,
            paddingRight: 25,
            paddingBottom: 25
          }}
        >
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={loading}
            onClick={handleProceedToLost}
          >
            Tag to Lost
          </Button>
          <div>
            <Button
              onClick={onClose}
              disabled={loading}
              style={{ marginRight: 15 }}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading}
            >
              {ticket?.id ? 'Save' : 'Add Ticket'}
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};
